// Article content rendered to HTML ahead of the build by
// scripts/generate-static-content.mjs. Edit these strings, then run
// `yarn generate:content` to refresh src/staticContent.generated.js.
//
// The keys are stable names used by <Equation> and <CodeBlock> in App.jsx.

export const equations = {
  'julia-with-i': 'z_{n+1} = z_n^2 + c, \\quad z_0 = x_0 + iy_0, \\quad{i = \\sqrt{-1}}',
  'julia-iteration': 'z_{n+1} = z_n^2 + c',
  'complex-number': 'z_0 = x_0 + iy_0, \\quad{i = \\sqrt{-1}}',
  'complex-parameter': 'c = c_x + ic_y',
  'real-part-iteration': 'x_{n+1} = x_n^2 - y_n^2 + c_x',
  'imaginary-part-iteration': 'y_{n+1} = 2x_ny_n + c_y',
};

// Code snippets are stored trimmed; the renderer highlights them as-is.
export const codeSnippets = {
  'iterate-until-escape': `const ESCAPE_THRESHOLD = 4;
const MAX_ITERATIONS = 1024;

function iterateUntilEscape(x0, y0, cx, cy) {
  let x = x0, y = y0;
  let numIterations = 0;
  while ((x * x + y * y <= ESCAPE_THRESHOLD) &&
         (numIterations < MAX_ITERATIONS)) {
    const xNext = x * x - y * y + cx;
    const yNext = 2 * x * y + cy;
    x = xNext;
    y = yNext;
    numIterations++;
  }
  const escaped = x * x + y * y > ESCAPE_THRESHOLD;
  return { numIterations, x, y, cx, cy, escaped };
}`,
  'iteration-color-palette': `const PALETTE_SIZE = 512;

function palettePhase(value, gScale = 7) {
  return (Math.sqrt(Math.max(0, value) + 1) - 1) *
    gScale / 55;
}

function samplePalette(value, paletteTable) {
  const phase = palettePhase(value);
  const position =
    (phase - Math.floor(phase)) * PALETTE_SIZE;
  const index = Math.floor(position);
  const fraction = position - index;
  const base0 = index * 4;
  const base1 = ((index + 1) % PALETTE_SIZE) * 4;
  const sampleChannel = c => {
    const c0 = paletteTable[base0 + c];
    const c1 = paletteTable[base1 + c];
    return Math.round(c0 + (c1 - c0) * fraction);
  };
  return [
    sampleChannel(0),
    sampleChannel(1),
    sampleChannel(2),
    255,
  ];
}

function getIterationColor(result, paletteTable) {
  const { numIterations } = result;
  return samplePalette(numIterations, paletteTable);
}`,
  'draw-fractal-canvas': `function drawJuliaSet(
  canvasEl, xRange, yRange, cx, cy,
  getColor, paletteTable
) {
  const canvasWidth = canvasEl.width;
  const canvasHeight = canvasEl.height;
  const context = canvasEl.getContext('2d');
  const canvasImageData =
    context.createImageData(canvasWidth, canvasHeight);
  const image = canvasImageData.data;
  for (let i = 0; i < canvasHeight; i++) {
    for (let j = 0; j < canvasWidth; j++) {
      const x0 = xRange[0] +
        j * (xRange[1] - xRange[0]) / canvasWidth;
      const y0 = yRange[0] +
        i * (yRange[1] - yRange[0]) / canvasHeight;
      const result = iterateUntilEscape(x0, y0, cx, cy);
      const pixelIndex = 4 * (i * canvasWidth + j);
      if (result.escaped) {
        const pixels = getColor(result, paletteTable);
        for (let p = 0; p < 4; p++) {
          image[pixelIndex + p] = pixels[p];
        }
      }
    }
  }
  context.putImageData(canvasImageData, 0, 0);
}`,
  'escape-time-coloring': `const ESCAPE_RADIUS = 2;
const SMOOTHING_STEPS = 3;

function getMu({ numIterations, x, y, cx, cy }) {
  for (let i = 0; i < SMOOTHING_STEPS; i++) {
    const xNext = x * x - y * y + cx;
    const yNext = 2 * x * y + cy;
    x = xNext;
    y = yNext;
  }
  const distanceSquared = x * x + y * y;
  return numIterations + SMOOTHING_STEPS + 1 -
    Math.log(0.5 * Math.log(distanceSquared)) /
    Math.log(ESCAPE_RADIUS);
}

function getSmoothColor(result, paletteTable) {
  const mu = getMu(result);
  return samplePalette(mu, paletteTable);
}`,
};
